import { Button } from "@/components/atoms/buttons";
import RadioShipping from "@/components/atoms/radio-shipping";
import ModalTemplate from "@/components/molecules/modal-template";
import EmptyAddressCard from "@/components/organisms/empty-address-card";
import { FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Locales } from "@/lib/i18n";
import { __ } from "@/lib/i18n/functions";
import { getAddressMetadata, getDifferentAddress } from "@/lib/utils";
import { DeliveryAndBillingSchema } from "@/lib/validation/payment";
import { useEffect, useState, useTransition } from "react";
import { useFormContext } from "react-hook-form";
import { StoreCustomerAddress } from "@medusajs/types";
import { sdk } from "@/lib/medusa/client";
import { toast } from "@/components/atoms/toast";
import { useCustomer } from "@/hooks/useCustomer";
import BillingAddressForm from "@/components/organisms/forms/checkout/billing-address-form";
import BillingAddressCard from "@/components/templates/Checkout/components/billing-address-card";
import { Loader2 } from "lucide-react";
import { useCheckoutStore } from "@/stores/checkout-store";
import {
  BILLING_ADDRESS_REQUIREMENT_MESSAGE,
  isBillingAddressEligibleForAccountType,
} from "@/lib/billing-address-eligibility";

type BillingDetailsProps = {
  locale: Locales;
  customerAddresses: StoreCustomerAddress[] | undefined;
  accountTypeValue: DeliveryAndBillingSchema["accountType"];
  shippingAddressValue: string;
};

const BillingDetails = ({ locale, customerAddresses, accountTypeValue, shippingAddressValue }: BillingDetailsProps) => {
  const {
    control,
    setValue,
    formState: { errors },
    getValues,
    clearErrors,
  } = useFormContext<DeliveryAndBillingSchema>();
  const { refreshCustomer } = useCustomer();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPending, startTransition] = useTransition();
  const { billingAddress } = getDifferentAddress(customerAddresses);
  const [deleteModalOpenId, setDeleteModalOpenId] = useState<string | null>(null);
  const [addressToEdit, setAddressToEdit] = useState<StoreCustomerAddress | null>(null);
  const setData = useCheckoutStore((state) => state.setData);
  const billingAddressFromStore = useCheckoutStore((state) => state.billingAddress);

  const filteredBillingAddress = billingAddress?.filter((address) =>
    isBillingAddressEligibleForAccountType(address, accountTypeValue)
  );

  useEffect(() => {
    if (filteredBillingAddress && filteredBillingAddress[0] && filteredBillingAddress.length === 1) {
      const currentValue = getValues("billingAddress");
      const addressId = filteredBillingAddress[0].id;

      if (currentValue !== addressId) {
        setValue("billingAddress", addressId);
        clearErrors("billingAddress");
      }
    }
  }, [filteredBillingAddress, setValue, clearErrors, getValues]);

  const handleAddNewAddress = () => {
    setAddressToEdit(null);
    setIsModalOpen(true);
  };

  const handleEditAddress = (address: StoreCustomerAddress) => {
    setAddressToEdit(address);
    setIsModalOpen(true);
  };

  const handleDeleteAddress = (addressId: string) => {
    startTransition(async () => {
      try {
        await sdk.store.customer.deleteAddress(addressId);
        await refreshCustomer();
        if (billingAddressFromStore && addressId === billingAddressFromStore) {
          setData({ billingAddress: "" });
          setValue("billingAddress", "");
          clearErrors("billingAddress");
        }
        toast.success({ title: __("Address deleted successfully", locale) });
        setDeleteModalOpenId(null);
      } catch (error) {
        toast.error({
          title: __("Error deleting address", locale),
          description: __("Please try again later.", locale),
        });
        console.error("Error deleting address:", error);
      }
    });
  };

  if (billingAddress === undefined) {
    return (
      <div className="flex h-40 items-center justify-center">
        <Loader2 className="animate-spin" />
      </div>
    );
  }

  if (filteredBillingAddress?.length === 0 || !filteredBillingAddress?.[0]) {
    return (
      <>
        <EmptyAddressCard
          locale={locale}
          modalTitle={__("Add a billing address", locale)}
          modalClassName="sm:max-w-[920px]"
        >
          <BillingAddressForm
            showPrefillCheckbox={!!shippingAddressValue}
            shippingAddressValue={shippingAddressValue}
            accountTypeValue={accountTypeValue}
            locale={locale}
            onSuccess={() => setIsModalOpen(false)}
          />
        </EmptyAddressCard>
        <p role="alert" className="text-pink-primary text-sm font-medium">
          {errors.billingAddress?.message || __(BILLING_ADDRESS_REQUIREMENT_MESSAGE, locale)}
        </p>
      </>
    );
  }

  return (
    <>
      {filteredBillingAddress && filteredBillingAddress.length >= 1 && (
        <FormField
          control={control}
          name="billingAddress"
          render={({ field }) => (
            <FormItem>
              {filteredBillingAddress.length > 1 ? (
                <RadioShipping
                  locale={locale}
                  name="billing-address"
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                  className="max-w-none gap-4"
                  hasBorder
                  isPending={isPending}
                  items={filteredBillingAddress.map((address) => {
                    const metadata = getAddressMetadata(address);

                    return {
                      value: address.id,
                      content: {
                        title: address.address_name || metadata.address_name,
                        description: address.address_1!,
                        postalCode: address.postal_code || "",
                        city: address.city || "",
                      },
                      onDelete: () => handleDeleteAddress(address.id),
                      onEdit: () => handleEditAddress(address),
                      deleteModalOpen: deleteModalOpenId === address.id,
                      onDeleteModalChange: (open: boolean) => setDeleteModalOpenId(open ? address.id : null),
                    };
                  })}
                />
              ) : (
                <BillingAddressCard
                  setValue={setValue}
                  type="checkout"
                  locale={locale}
                  address={filteredBillingAddress[0]}
                />
              )}
              <FormMessage />
            </FormItem>
          )}
        />
      )}
      <ModalTemplate
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        className="sm:max-w-[920px]"
        classNameTitle="font-cabinet-grotesk leading-h1 text-lg md:text-1xl"
        title={__(addressToEdit ? "Edit billing address" : "Add a billing address", locale)}
        triggerAsChild
        modalTrigger={
          <Button
            variant="secondary"
            size="lg"
            className="border-primary-light/50 font-cabinet-grotesk bg-inherit text-lg font-semibold max-sm:h-12 sm:text-xl"
            onClick={handleAddNewAddress}
          >
            {__("Add an address", locale)}
          </Button>
        }
      >
        <BillingAddressForm
          showPrefillCheckbox={!!shippingAddressValue}
          shippingAddressValue={shippingAddressValue}
          accountTypeValue={addressToEdit ? (addressToEdit.company ? "company" : "individual") : accountTypeValue}
          locale={locale}
          onSuccess={() => {
            setIsModalOpen(false);
            setAddressToEdit(null);
          }}
          addressToEdit={addressToEdit}
          addressId={addressToEdit?.id}
        />
      </ModalTemplate>
    </>
  );
};

export default BillingDetails;
